/**
 * @file    SortableListItem.tsx
 * @scope   SHARED
 * @summary Generic card shell for sortable lists: drag handle, content slot, delete icon and
 *          validation tip.
 * @consumes ControlState, DataValidationTip
 * @produces SortableListItem
 * @sideEffects None.
 * @notes Presenter only. Domain content (labels, counters, links) comes via `children`.
 */
import React, { useState } from 'react';
import { Icon } from '@cx/ui';
import DataValidationTip from 'components/DataValidation/DataValidationTip';
import { ControlState } from '../../models/controlState';
import './SortableListItem.scss';

/** Elements where the pointer must select/edit text instead of dragging the row. */
const EDITABLE_SELECTOR = 'input, textarea, select, [contenteditable="true"]';

const isEditableTarget = (target: EventTarget | null): boolean =>
  target instanceof Element && !!target.closest(EDITABLE_SELECTOR);

export interface SortableListItemProps {
  id: string;
  index: number;
  dragState: ControlState;
  deleteState: ControlState;
  isInvalid?: boolean;
  /** Tooltip shown next to the delete icon when `isInvalid`. */
  invalidMessage?: string;
  onDragStart: (e: React.DragEvent<HTMLDivElement>, id: string) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>, index: number) => void;
  onDelete: (id: string) => void;
  /** Called when focus moves from inside the row to somewhere outside it. */
  onFocusLeave?: (id: string) => void;
  children: React.ReactNode;
}

const buildClassName = (base: string, modifier: string, active?: boolean) =>
  active ? `${base} ${modifier}` : base;

const SortableListItem: React.FC<SortableListItemProps> = ({
  id,
  index,
  dragState,
  deleteState,
  isInvalid = false,
  invalidMessage,
  onDragStart,
  onDragOver,
  onDrop,
  onDelete,
  onFocusLeave,
  children,
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) =>
    setIsEditing(isEditableTarget(e.target));

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    if (isEditableTarget(e.target)) {
      e.preventDefault();
      return;
    }
    onDragStart(e, id);
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const next = e.relatedTarget as Node | null;
    if (!next || !e.currentTarget.contains(next)) onFocusLeave?.(id);
  };

  const handleDelete = () => {
    if (!deleteState.disabled) onDelete(id);
  };

  const deleteClassName = [
    'diy-drawer-icon deleteIcon',
    deleteState.disabled ? 'deleteIcon--disabled' : '',
    deleteState.visible ? '' : 'deleteIcon--hidden',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      tabIndex={-1}
      onBlur={handleBlur}
      draggable={!dragState.disabled && !isEditing}
      onPointerDownCapture={handlePointerDown}
      onDragStart={handleDragStart}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, index)}
      className={buildClassName('dataSourceItem', 'dataSourceItem--invalid', isInvalid)}
    >
      <div className="dataSourceItemContent">
        {dragState.visible && (
          <div className="dragHandle" title="Drag to reorder">
            <Icon name="drag_indicator" />
          </div>
        )}
        <div className="dataSourceInfo">{children}</div>
        <div className="partitionRightIconsWrapper">
          <div className={deleteClassName} onClick={handleDelete}>
            <Icon name="system-delete" />
          </div>
          <div className="deleteIconWrapper">
            {isInvalid && invalidMessage && (
              <DataValidationTip message={invalidMessage} kind="error" placement="bottom" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SortableListItem;
